// @ts-check
import { values, keyBy, forEach, map, groupBy } from "lodash";

import {
  getPlannerColumns,
  getPlannerMacroAllocations,
  checkForPending,
  setGroupingsWithActualConstraints,
  setGroupingsWithDefaultConstraints,
  setGroupingsWithPlannerCells,
  setCalculatedSumTotals,
  setNotifications,
  pruneEmptyGroupings,
  pruneEmptyLineItems,
  buildGroupingLookup,
  buildCellLookup,
  hashGrouping,
  buildGroupLookup,
  setGroupingsBudget,
  setGroupingsTotalCost,
  buildLineItemConstraintLookups,
} from "../util";

import buildGroupings from "./buildGroupings/byDemand";
import setGroupingsWithLineItems from "./setLineItems/supplyLineItems";

// TODO: change signature to named props
const buildPlannerGroupedByDemand = ({
  rootGroup,
  data,
  hideHiddenTeams,
  showObjectives,
  groupTypes,
  shouldHash,
  isBudgetEnabled,
  useDeltaForBudget,
  overBudgetLimit,
  underBudgetLimit,
  isRealtimeFteMode,
  useRealtimeChangeOnly,
  useRealtimeFteDelta,
}) => {
  const groupLookup = buildGroupLookup({
    macroAllocationGroups: data.macroAllocationGroups,
    descendantSupplyGroups: data.descendantSupplyGroups,
    roleAreaGroups: data.roleAreaGroups,
    relatedSupplyGroups: data.relatedSupplyGroups,
    descendantTargetGroups: data.descendantTargetGroups,
    relatedBudgetTargetGroups: data.relatedBudgetTargetGroups,
    groupTypes,
  });

  const columns = getPlannerColumns(data.allocationProjects);
  const allocationProjectLookup = keyBy(
    map(columns, (c) => c.allocationProject),
    "id"
  );

  const macroAllocations = getPlannerMacroAllocations({
    macroAllocations: data.macroAllocations,
    allocationProjectLookup,
    includeNullStatus: !rootGroup.isSource,
    groupedByDemand: true,
  });
  const constraints = groupBy(data.constraints, "groupId");

  const groupings = buildGroupings({
    rootGroup,
    groupLookup,
    hideHiddenTeams,
    showObjectives,
    groupTypes,
    columnCount: columns.length,
    isBudgetEnabled,
    allocationProjectLookup,
  });
  setGroupingsWithLineItems({
    groupings,
    macroAllocations,
    columnCount: columns.length,
    groupTypes,
    canRename: rootGroup.isSource,
    isBudgetEnabled,
    groupLookup,
    hideHiddenTeams,
  });
  setGroupingsWithPlannerCells({
    groupings,
    macroAllocations,
    allocationProjectLookup,
    isRequestor: !rootGroup.isSource,
    groupedByDemand: true,
    useDeltaForBudget,
    isRealtimeFteMode: rootGroup.isDemand && isRealtimeFteMode,
    useRealtimeChangeOnly: rootGroup.isDemand && useRealtimeChangeOnly,
    // Only use the realtime fte delta for demand root group
    useRealtimeFteDelta: rootGroup.isDemand && useRealtimeFteDelta,
  });
  setGroupingsWithActualConstraints({
    groupings,
    constraints,
    allocationProjectLookup,
    columns,
  });
  setGroupingsWithDefaultConstraints({
    groupings,
    allocationProjectLookup,
    columns,
  });

  if (isBudgetEnabled) {
    setGroupingsBudget({
      groupings,
      relatedSupplyGroups: data.relatedSupplyGroups,
      roleAreaGroups: data.roleAreaGroups,
      allocationProjectLookup,
    });
    setGroupingsTotalCost(groupings, overBudgetLimit, underBudgetLimit);
  }

  setCalculatedSumTotals(groupings, columns);
  setNotifications(groupings, columns);

  pruneEmptyLineItems(groupings);
  if (rootGroup.isSource) {
    pruneEmptyGroupings(groupings, isBudgetEnabled);
  }

  const columnLookup = keyBy(columns, "id");
  const groupingLookup = buildGroupingLookup(groupings, {});
  const { lineItemLookup, constraintCellLookup } =
    buildLineItemConstraintLookups(values(groupingLookup), {}, {});
  const cellLookup = buildCellLookup(values(lineItemLookup), {});
  const isPendingSubmit = checkForPending(groupingLookup);

  // Hash groupings in case anything changed
  if (shouldHash) {
    // @ts-ignore
    forEach(groupings, hashGrouping);
  }

  return {
    isPendingSubmit,
    columns,
    groupings,
    // lookup objects: for when we need fast access to objects in the tree
    lookups: {
      allocationProjectLookup,
      columnLookup,
      groupLookup,
      groupingLookup,
      lineItemLookup,
      cellLookup,
      constraintCellLookup,
      macroAllocationLookup: macroAllocations,
    },
  };
};

export default buildPlannerGroupedByDemand;
